import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Users, Building2, Award, Calendar, Languages, GraduationCap, BookOpen } from 'lucide-react';
import { services, stats, testimonials } from '../data/mock';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1663968473220-89486bb8e4d3"
            alt="Prague"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/85 to-gray-900/70"></div>
        </div>
        
        <div className="container mx-auto px-4 z-10 relative">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Çekya'da Eğitim<br />
              <span className="text-amber-500">Hayalinizi</span> Gerçekleştirin
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Dil kursundan üniversite başvurusuna, vizeden konaklamaya kadar tüm süreçlerde yanınızdayız. 
              Profesyonel danışmanlık ile Avrupa'nın kalbinde eğitim alın.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/iletisim"
                className="bg-amber-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-amber-700 transition-all shadow-lg hover:shadow-xl flex items-center gap-2 group"
              >
                Ücretsiz Danışmanlık Alın
                <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
              </Link>
              <Link
                to="/hizmetler"
                className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
              >
                Hizmetlerimizi Keşfedin
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-amber-600">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => {
              const IconComponent = {
                Users,
                Building2,
                Award,
                Calendar
              }[stat.icon];
              return (
                <div key={stat.id} className="text-center text-white">
                  <div className="flex justify-center mb-3">
                    <IconComponent size={36} />
                  </div>
                  <div className="text-4xl font-bold mb-2">{stat.number}</div>
                  <div className="text-amber-100">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Hizmetlerimiz</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Çekya'da eğitim sürecinizin her aşamasında profesyonel destek
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = {
                Languages,
                GraduationCap,
                BookOpen
              }[service.icon];
              return (
                <div
                  key={service.id}
                  className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all overflow-hidden group cursor-pointer"
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="text-amber-600" size={28} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <ul className="space-y-2 mb-4">
                      {service.features.map((feature, index) => (
                        <li key={index} className="flex items-center gap-2 text-sm text-gray-700">
                          <CheckCircle className="text-green-500 flex-shrink-0" size={16} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/hizmetler"
                      className="text-amber-600 font-semibold flex items-center gap-1 hover:gap-2 transition-all"
                    >
                      Detaylı Bilgi
                      <ArrowRight size={18} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1613002928486-12d93bd86e1a"
                alt="Prague University"
                className="rounded-2xl shadow-2xl"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Neden Arteğitim?</h2>
              <p className="text-lg text-gray-600 mb-8">
                Yılların deneyimi ve yüzlerce başarılı öğrenci hikayesiyle, Çekya'da eğitim almanız için en güvenilir çözüm ortağınız.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="text-amber-600" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900 mb-1">Profesyonel Danışmanlık</h3>
                    <p className="text-gray-600">Deneyimli ekibimizle size özel çözümler sunuyoruz.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="text-amber-600" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900 mb-1">Yerel Destek</h3>
                    <p className="text-gray-600">Prag'da ofisimizle sürekli yanınızdayız.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="text-amber-600" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900 mb-1">Uygun Fiyat</h3>
                    <p className="text-gray-600">Şeffaf ve rekabetçi fiyatlandırma politikamız.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="text-amber-600" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900 mb-1">Baştan Sona Hizmet</h3>
                    <p className="text-gray-600">Başvurudan yerleşime kadar tüm süreçte yanınızdayız.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Öğrenci Yorumları</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Çekya'da eğitim alan öğrencilerimizin deneyimleri
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.slice(0, 3).map((testimonial) => (
              <div key={testimonial.id} className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center gap-4 mb-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-sm text-gray-600">{testimonial.program}</p>
                  </div>
                </div>
                <div className="flex gap-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 italic">"{testimonial.comment}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-amber-600 to-amber-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Hayalinizdeki Eğitim İçin İlk Adım</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-amber-50">
            Ücretsiz danışmanlık için bugün bizimle iletişime geçin. Size özel çözümler sunalım.
          </p>
          <Link
            to="/iletisim"
            className="inline-flex items-center gap-2 bg-white text-amber-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-xl group"
          >
            Hemen Başvurun
            <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;